import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ProfileComponent.css';
import { useUser } from '../../../../auth/UserContext';
import { findUserProfile, saveProfile } from '../../../../service/profile/profile-service';
import { useAuth0 } from '../../../../auth/useAuth0';
import PersonalDetailsComponent from './components/PersonalDetailsComponent';
import { UserProfile } from '../../../../model/user/profile/UserProfile';
import { User } from "../../../../model/User";
import MyAccountComponent from "./components/MyAccountComponent";
import {useAlert} from "../../../../context/alert/AlertContext";
import ActionsButtonsComponent from "../../../commons/action-buttons/ActionsButtonsComponent";

const ProfileComponent: React.FC = () => {
    const { t } = useTranslation();
    const { user, setUser } = useUser();
    const { getAccessTokenSilently, logout } = useAuth0();

    const [selectedProfileSideTab, setSelectedProfileSideTab] = useState('Personal Details');
    const [profileData, setProfileData] = useState<UserProfile | null>(null);
    const [isModified, setIsModified] = useState(false);

    const { setAlert } = useAlert();

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const token = await getAccessTokenSilently();
                const fetchedProfile = await findUserProfile(token);
                if (fetchedProfile) {
                    if (user?.email !== fetchedProfile.email){
                        await logout()
                    }
                    setProfileData(fetchedProfile);
                }
            } catch (error) {
                console.error('Error fetching user profile:', error);
                setAlert('danger', `Error fetching user profile: ${error}`);
            }
        };

        fetchUserProfile();
    }, [getAccessTokenSilently, logout, user?.email]);

    const handleSave = async () => {
        if (profileData) {
            try {
                const token = await getAccessTokenSilently();
                if (user?.email !== profileData.email){
                    await logout()
                }
                const result = await saveProfile(profileData, token);

                if (result.success) {
                    console.log('Profile saved successfully:', result.data);
                    setIsModified(false);
                    setAlert('success', `Profile saved successfully`);
                    const updatedUser: User = {
                        ...user,
                        firstName: profileData.firstName,
                        lastName: profileData.lastName,
                        image: profileData.image,
                        phone: profileData.phoneNumber,
                        language: profileData.language
                    };
                    setUser(updatedUser);
                } else {
                    console.error('Failed to save profile:', result.error);
                    setAlert('danger', `Failed to save profile: ${result.error}`);

                }
            } catch (error) {
                console.error('Error during save:', error);
                setAlert('danger', `An error occurred during saving: ${error}`);
            }
        }
    };

    const handleCancel = () => {
        setIsModified(false);
    };

    const renderProfileContent = () => {
        switch (selectedProfileSideTab) {
            case 'Personal Details':
                return (
                    <PersonalDetailsComponent
                        profileData={profileData}
                        setProfileData={setProfileData}
                        setIsModified={setIsModified}
                    />
                );
            case 'Get Service Account':
                return <MyAccountComponent profileData={profileData} />;
            default:
                return null;
        }
    };

    return (
        <div className="profile-content">
            <div className="side-menu">
                <button
                    data-cy="profile-tab-personal-details"
                    className={`lateral-button ${selectedProfileSideTab === 'Personal Details' ? 'active' : ''}`}
                    onClick={() => setSelectedProfileSideTab('Personal Details')}
                >
                    {t('settings.profile.tabs.personalDetails')}
                </button>
                <button
                    data-cy="profile-tab-account"
                    className={`lateral-button ${selectedProfileSideTab === 'Get Service Account' ? 'active' : ''}`}
                    onClick={() => setSelectedProfileSideTab('Get Service Account')}
                >
                    {t('settings.profile.tabs.account')}
                </button>
            </div>
            <div className="side-content-area">
                <ActionsButtonsComponent
                    isModified={isModified}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />
                {renderProfileContent()}
            </div>
        </div>
    );
};

export default ProfileComponent;
